<template>
  <div v-if="loaded" id="product_aside" class="product-aside">
    <section class="product-aside-heading">
      <Heading :product="product">
        <Price :product="product" :validInCurrentMarket="validInCurrentMarket" />
        <Reviews
          v-if="product.product_reviews && product.product_reviews.aggregate_summary && (product.product_reviews.aggregate_summary.stars_average_rounded_to_fives >= 4)"
          :count="product.product_reviews.aggregate_summary.number_of_reviews.total"
          :rating="product.product_reviews.aggregate_summary.stars_average_rounded_to_fives"
        />
        <MarketExclusiveLabel v-if="!validInCurrentMarket" />
        <Description :product="product" />

        <span v-if="upstreamConnectionFailure" class="product-aside-heading-upsteam-connection-error">
          ⚠️ There is currently an issue connecting to the stock control system. Please enter your email below and we can alert you as soon as this is resolved.
        </span>

        <Actions
          ref="mainAction"
          v-if="showProductActions"
          :product="product"
          :variantStock="variantStock"
          :isOutOfStock="isOutOfStock"
          :upstreamConnectionFailure="upstreamConnectionFailure"
        />
        <EnquireNow :product="product" />
      </Heading>
      <Delivery v-if="feature.product_page_ux !== '1_0'" />
    </section>

    <div
      v-if="feature.product_page_ux !== '1_0' && validInCurrentMarket"
      class="sticky-add-to-cart"
      :class="{ 'sticky-add-to-cart--active': stickyActive }"
    >
      <div class="sticky-add-to-cart-actions">
        <Actions
          v-if="showProductActions"
          :product="product"
          :variantStock="variantStock"
          :isOutOfStock="isOutOfStock"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex"

import globalTheme from "Assets/js/components/globalTheme"
import stockService from "Assets/js/components/stockService"
import marketExclusiveService from "Assets/js/components/marketExclusiveService"
import Heading from "./Heading/index.vue"
import Reviews from "./Heading/reviews.vue"
import Price from "./Price/index.vue"
import Actions from "./Actions/index.vue"
import Description from "./Description/index.vue"
import Messages from "./Messages/index.vue"
import productService from "Assets/js/components/productService"
import MarketExclusiveLabel from "Templates/pages/productAside/MarketExclusiveLabel/index.vue"
import EnquireNow from "./EnquireNow/index.vue"
import Delivery from "./Delivery/index.vue"

export default {
  name: "ProductAside",
  components: {
    Reviews,
    MarketExclusiveLabel,
    Description,
    Heading,
    Messages,
    Actions,
    EnquireNow,
    Price,
    Delivery
  },
  data() {
    return {
      upstreamConnectionFailure: false,
      loaded: false,
      stickyActive: false,
      variantStock: [],
      marketExclusive: false,
      marketExclusiveB2B: false,
      availableMarkets: [],
      notInMarketCatalog: false,
      id: null,
      product: {
        product_type: "",
        shopify_title: "",
        description: "",
        shopify_metafields: {
          coming_soon: false
        }
      },
      personalisationRequired: false,
      stockCount: 99,
      isOutOfStock: false,
      wishlist_enabled: false,
      onScrollHandler: null
    }
  },
  async beforeMount() {
    this.id = (this.$parent?.id ?? "").replace("gid://shopify/Product/", "")
    if (this.id) {
      await this.fetchProduct()
      await this.stockcheck()
      this.loaded = true
      this.marketExclusiveB2B = globalTheme.marketExclusive().b2b
      this.marketExclusive = await marketExclusiveService.getExclusive(this.id)
      this.availableMarkets = await marketExclusiveService.getAvailableMarkets(this.id)
    }
  },
  mounted() {
    this.onScrollHandler = () => this.updateSticky()
    window.addEventListener("scroll", this.onScrollHandler, { passive: true })
    window.addEventListener("resize", this.onScrollHandler, { passive: true })
    this.updateSticky()
  },
  beforeDestroy() {
    if (this.onScrollHandler) {
      window.removeEventListener("scroll", this.onScrollHandler)
      window.removeEventListener("resize", this.onScrollHandler)
    }
  },
  methods: {
    updateSticky() {
      const enquireComp = this.$refs.mainAction
      const el = enquireComp && enquireComp.$el ? enquireComp.$el : enquireComp
      if (!el || !el.getBoundingClientRect) {
        this.stickyActive = false
        return
      }

      const rect = el.getBoundingClientRect()
      this.stickyActive = rect.bottom <= 0
    },
    async fetchProduct() {
      this.productService = productService.getInstance()
      this.product = await new Promise((resolve) => {
        this.productService.checkProduct((product) => resolve(product))
      })
    },
    async stockcheck() {
      if (this.product.shopify_metafields.coming_soon) {
        this.loaded = true
        this.stockCount = 0
        this.isOutOfStock = true
        return
      }

      if (!this.product.tracksInventory) {
        this.loaded = true
        this.stockCount = 999
        this.isOutOfStock = false
        return
      }

      this.stockService = stockService.getInstance()
      await new Promise((resolve) => {
        this.stockService.checkStock(
          "gid://shopify/Product/" + this.id,
          (responseData) => {
            this.upstreamConnectionFailure = false;
            this.notInMarketCatalog = false;
            this.variantStock = responseData.variants?.edges ?? []
            this.isOutOfStock = !responseData?.availableForSale && !this.product?.shopify_metafields?.enquire_now
            this.stockCount = responseData?.totalInventory ?? 99
            if (responseData?.availableForSale && responseData?.totalInventory <= 0) {
              this.stockCount = 99
            }
            resolve()
          },
          (error) => {
            this.loaded = true;
            this.upstreamConnectionFailure = true;
          },
          () => {
            // Not in this market's catalog: Shopify would refuse it at checkout.
            this.notInMarketCatalog = true;
            this.upstreamConnectionFailure = false;
            this.isOutOfStock = false;
            this.loaded = true;
            resolve()
          })
      })
    }
  },
  computed: {
    ...mapGetters(["isB2B", "isMobile", "currentMarket", "feature"]),
    validInCurrentMarket() {
      // The live catalog check beats the stored market list: Shopify itself has
      // just told us it will not deliver this product to this market (OSS-2671).
      if (this.notInMarketCatalog) {
        return false
      }
      if (this.isB2B && this.availableMarkets.length && !this.marketExclusiveB2B) {
        return false
      }
      if (this.availableMarkets.length) {
        return this.availableMarkets.indexOf(this.currentMarket) !== -1
      }
      return !this.marketExclusive
    },
    priceClass() {
      return this.showOutOfStock ? "product-aside-price product-aside-price--outofstock" : "product-aside-price"
    },
    priceExVatClass() {
      return this.showOutOfStock
        ? "product-aside-price-exvat product-aside-price-exvat--outofstock"
        : "product-aside-price-exvat product-aside-price-exvat"
    },
    showMarketExclusive() {
      if (this.isB2B && this.availableMarkets.length && !this.marketExclusiveB2B) {
        return false
      }
      if (!this.isB2B && this.availableMarkets.length) {
        return this.availableMarkets.indexOf(this.currentMarket) !== -1
      }
      return false
    },
    showProductActions() {
      if (this.notInMarketCatalog) {
        return false
      }
      if (!this.isB2B && this.availableMarkets.length) {
        return this.availableMarkets.indexOf(this.currentMarket) !== -1
      }
      if (this.isB2B && this.availableMarkets.length && !this.marketExclusiveB2B) {
        return false
      }
      return true
    }
  }
}
</script>
